/* ==========================================================================
   FITOOR — theme.js
   Smooth scroll, cinematic motion, cart + product logic. No dependencies
   besides Lenis (smooth scrolling), which is loaded before this file.
   ========================================================================== */
(() => {
  'use strict';

  const html = document.documentElement;
  const T = window.theme || {};
  const R = T.routes || {};
  const S = T.strings || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const designMode = !!T.designMode || !!(window.Shopify && window.Shopify.designMode);

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const pad = (n) => String(n).padStart(2, '0');

  /* ---------------------------------------------------------------------
     Frame loop + smooth scroll
     --------------------------------------------------------------------- */
  const tickers = new Set();
  const scroll = { y: window.scrollY, last: window.scrollY, v: 0 };
  let lenis = null;

  const onTick = (fn, el) => {
    const wrapped = (t) => {
      if (el && !el.isConnected) {
        tickers.delete(wrapped);
        return;
      }
      fn(t);
    };
    tickers.add(wrapped);
  };

  const frame = (t) => {
    if (lenis) lenis.raf(t);
    const y = window.scrollY;
    scroll.v = lenis ? lenis.velocity : y - scroll.last;
    scroll.last = y;
    scroll.y = y;
    tickers.forEach((fn) => fn(t));
    requestAnimationFrame(frame);
  };

  const initLenis = () => {
    if (!T.smoothScroll || reduce || designMode || isTouch || !window.Lenis) return;
    lenis = new window.Lenis({ lerp: 0.085, smoothWheel: true, wheelMultiplier: 1 });
    html.classList.add('has-lenis');
  };

  let locks = 0;
  const lockScroll = (on) => {
    locks = Math.max(0, locks + (on ? 1 : -1));
    const locked = locks > 0;
    if (lenis) {
      if (locked) lenis.stop();
      else lenis.start();
    }
    html.style.overflow = locked ? 'hidden' : '';
  };

  const scrollToTarget = (target, offset = 0) => {
    if (lenis) lenis.scrollTo(target, { offset, duration: 1.6 });
    else {
      const top = typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY + offset;
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    }
  };

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */
  const toast = (msg) => {
    const t = $('[data-toast]');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove('is-visible'), 3400);
  };

  const formatNumber = (cents, decimals, thousands = ',', decimal = '.') => {
    const fixed = (cents / 100).toFixed(decimals);
    const parts = fixed.split('.');
    return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
  };

  const money = (cents) => {
    const fmt = String(T.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    let value;
    switch (key) {
      case 'amount_no_decimals':
        value = formatNumber(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatNumber(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatNumber(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatNumber(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatNumber(cents, 2, ' ', ',');
        break;
      default:
        value = formatNumber(cents, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ---------------------------------------------------------------------
     Preloader
     --------------------------------------------------------------------- */
  const ready = () => {
    if (html.classList.contains('is-ready-fired')) return;
    html.classList.add('is-ready', 'is-ready-fired');
  };

  const initPreloader = () => {
    const el = $('[data-preloader]');
    if (!el || html.classList.contains('skip-preloader') || getComputedStyle(el).display === 'none') {
      requestAnimationFrame(ready);
      return;
    }
    // The timeout in <head> is a safety net only; cancel early reveal while we play the intro.
    html.classList.remove('is-ready');
    lockScroll(true);
    const count = $('[data-preloader-count]', el);
    const bar = $('[data-preloader-bar]', el);
    const duration = reduce ? 200 : 1900;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 4);
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      el.classList.add('is-done');
      setTimeout(ready, 280);
      setTimeout(() => {
        el.remove();
        lockScroll(false);
      }, 1300);
      try {
        sessionStorage.setItem('fitoor:seen', '1');
      } catch (e) {
        /* storage unavailable */
      }
    };

    const step = (now) => {
      const t = clamp((now - start) / duration, 0, 1);
      const e = ease(t);
      if (count) count.textContent = String(Math.round(e * 100)).padStart(3, '0');
      if (bar) bar.style.transform = `scaleX(${e})`;
      if (t < 1) requestAnimationFrame(step);
      else finish();
    };
    requestAnimationFrame(step);
    setTimeout(finish, 4000);
  };

  /* ---------------------------------------------------------------------
     Page transitions (fade to black)
     --------------------------------------------------------------------- */
  const initTransitions = () => {
    if (!T.transitions || designMode) return;
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a[href]');
      if (!a) return;
      if ((a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      const href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript|sms):/i.test(href)) return;
      let url;
      try {
        url = new URL(a.href, window.location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      html.classList.add('is-leaving');
      setTimeout(() => {
        window.location.href = url.href;
      }, reduce ? 0 : 420);
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) html.classList.remove('is-leaving');
    });
  };

  /* ---------------------------------------------------------------------
     Reveals + split text
     --------------------------------------------------------------------- */
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => {
              if (en.isIntersecting) {
                en.target.classList.add('is-in');
                io.unobserve(en.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  const splitWords = (el) => {
    if (el.dataset.splitDone) return;
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            const w = document.createElement('span');
            w.className = 'w';
            const inner = document.createElement('span');
            inner.className = 'w__i';
            inner.style.setProperty('--i', i++);
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') {
          walk(n);
        }
      });
    };
    walk(el);
    el.dataset.splitDone = '1';
  };

  const initReveals = (root = document) => {
    $$('[data-split]', root).forEach(splitWords);
    $$('[data-stagger]', root).forEach((p) => Array.from(p.children).forEach((c, i) => c.style.setProperty('--i', i)));
    $$('[data-reveal]', root).forEach((el) => {
      if (!io || reduce) el.classList.add('is-in');
      else io.observe(el);
    });
  };

  const revealNow = (root) => {
    $$('[data-split]', root).forEach(splitWords);
    $$('[data-reveal]', root).forEach((el) => el.classList.add('is-in'));
  };

  /* ---------------------------------------------------------------------
     Cursor, magnetic, spotlight
     --------------------------------------------------------------------- */
  const initCursor = () => {
    if (isTouch || !T.cursor) return;
    html.classList.add('has-cursor');
  };

  const initMagnetic = (root = document) => {
    if (isTouch || reduce) return;
    $$('[data-magnetic]', root).forEach((el) => {
      if (el._mag) return;
      el._mag = true;
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * 0.28;
        const y = (e.clientY - r.top - r.height / 2) * 0.38;
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
      });
    });
  };

  const initSpotlight = (root = document) => {
    $$('[data-spotlight]', root).forEach((el) => {
      if (el._spot) return;
      el._spot = true;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${e.clientX - r.left}px`);
        el.style.setProperty('--my', `${e.clientY - r.top}px`);
      });
    });
  };

  /* ---------------------------------------------------------------------
     Header + menu
     --------------------------------------------------------------------- */
  const setMenu = (open) => {
    const btn = $('[data-menu-toggle]');
    const menu = $('[data-menu]');
    if (!menu) return;
    const isOpen = html.classList.contains('menu-open');
    if (open === isOpen) return;
    html.classList.toggle('menu-open', open);
    if (btn) btn.setAttribute('aria-expanded', String(open));
    menu.setAttribute('aria-hidden', String(!open));
    lockScroll(open);
  };

  const initHeader = (root = document) => {
    const h = $('[data-header]', root) || $('[data-header]');
    if (!h || h._init) return;
    h._init = true;
    let last = window.scrollY;
    h.classList.toggle('is-scrolled', last > 30);
    onTick(() => {
      const y = window.scrollY;
      if (Math.abs(y - last) < 3) return;
      h.classList.toggle('is-scrolled', y > 30);
      if (!html.classList.contains('menu-open')) {
        if (y > last && y > 260) h.classList.add('is-hidden');
        else if (y < last) h.classList.remove('is-hidden');
      }
      last = y;
    }, h);
  };

  /* ---------------------------------------------------------------------
     Marquee (scroll-velocity reactive)
     --------------------------------------------------------------------- */
  const initMarquee = (root = document) => {
    $$('[data-marquee]', root).forEach((track) => {
      if (track._mq) return;
      track._mq = true;
      const speed = parseFloat(track.dataset.speed || '0.6');
      const reactive = track.hasAttribute('data-reactive');
      let x = 0;
      let half = 0;
      let visible = true;
      let dir = -1;
      const measure = () => {
        half = track.scrollWidth / 2;
      };
      measure();
      window.addEventListener('resize', measure);
      if (document.fonts) document.fonts.ready.then(measure);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          visible = en.isIntersecting;
        }).observe(track);
      }
      if (reduce) return;
      onTick(() => {
        if (!visible || !half) return;
        let v = speed;
        if (reactive) {
          const sv = scroll.v;
          if (Math.abs(sv) > 0.5) dir = sv > 0 ? -1 : 1;
          v = speed + Math.min(Math.abs(sv) * 0.3, 16);
        }
        x += v * dir;
        if (x <= -half) x += half;
        if (x > 0) x -= half;
        track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`;
      }, track);
    });
  };

  /* ---------------------------------------------------------------------
     Hero — WebGL smoke + scroll choreography
     --------------------------------------------------------------------- */
  const hexToRgb = (hex) => {
    const h = String(hex || '#ffffff').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  };

  const SMOKE_FRAG = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
      precision highp float;
    #else
      precision mediump float;
    #endif
    uniform vec2 uRes;
    uniform float uTime;
    uniform vec2 uMouse;
    uniform vec3 uC1;
    uniform vec3 uC2;
    uniform vec3 uBg;
    uniform float uScroll;

    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }
    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
                 mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
    }
    float fbm(vec2 p) {
      float v = 0.0;
      float a = 0.5;
      mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
      for (int i = 0; i < 5; i++) {
        v += a * noise(p);
        p = m * p;
        a *= 0.5;
      }
      return v;
    }
    void main() {
      vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
      float t = uTime * 0.05;
      vec2 q = vec2(fbm(p * 1.3 + vec2(0.0, t)), fbm(p * 1.3 + vec2(5.2, -t)));
      vec2 r = vec2(fbm(p * 1.6 + 1.8 * q + vec2(1.7, 9.2) + 0.6 * t),
                    fbm(p * 1.6 + 1.8 * q + vec2(8.3, 2.8) - 0.4 * t));
      float f = fbm(p * 1.1 + 1.6 * r + uMouse * 0.6);
      vec2 m = uMouse * 0.35;
      float l1 = smoothstep(1.25, 0.0, length(p - vec2(0.55, -0.3) - m));
      float l2 = smoothstep(1.3, 0.0, length(p - vec2(-0.6, 0.4) + m));
      // Light mode: tinted smoke is painted onto the page background
      // instead of being added as light onto black.
      vec3 col = uBg;
      col = mix(col, uC1, clamp(pow(f, 2.4) * l1 * 2.1, 0.0, 1.0) * 0.85);
      col = mix(col, uC2, clamp(pow(f, 2.2) * l2 * 1.8, 0.0, 1.0) * 0.8);
      col = mix(col, uC1, smoothstep(0.75, 0.0, length(p - vec2(0.0, -0.08))) * 0.22 * (0.6 + 0.4 * f));
      col = mix(col, mix(uC1, uC2, 0.5), pow(clamp(r.x * r.y, 0.0, 1.0), 3.0) * 0.3);
      float vig = smoothstep(1.35, 0.2, length(p * vec2(0.85, 1.1)));
      col = mix(uBg, col, vig * (1.0 - uScroll * 0.6));
      col += (hash(gl_FragCoord.xy + fract(uTime) * 91.7) - 0.5) * 0.015;
      gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
    }
  `;

  const createSmoke = (canvas) => {
    let gl;
    try {
      gl = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false, stencil: false, powerPreference: 'low-power' });
    } catch (e) {
      gl = null;
    }
    if (!gl) return null;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    };
    const vs = compile(gl.VERTEX_SHADER, 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }');
    const fs = compile(gl.FRAGMENT_SHADER, SMOKE_FRAG);
    if (!vs || !fs) return null;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const u = (n) => gl.getUniformLocation(prog, n);
    const uRes = u('uRes');
    const uTime = u('uTime');
    const uMouse = u('uMouse');
    const uC1 = u('uC1');
    const uC2 = u('uC2');
    const uBg = u('uBg');
    const uScroll = u('uScroll');
    gl.uniform3fv(uC1, hexToRgb(canvas.dataset.c1));
    gl.uniform3fv(uC2, hexToRgb(canvas.dataset.c2));
    gl.uniform3fv(uBg, hexToRgb(canvas.dataset.bg || '#f6ece6'));

    let w = 1;
    let h = 1;
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      let scale = Math.min(window.devicePixelRatio || 1, 2) * 0.5;
      const budget = 520000;
      if (r.width * r.height * scale * scale > budget) scale = Math.sqrt(budget / (r.width * r.height));
      w = Math.max(1, Math.round(r.width * scale));
      h = Math.max(1, Math.round(r.height * scale));
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    };
    resize();
    window.addEventListener('resize', resize);
    requestAnimationFrame(() => canvas.classList.add('is-live'));

    let drawn = false;
    return {
      render(t, mx, my, p) {
        if (reduce && drawn) return;
        gl.uniform2f(uRes, w, h);
        gl.uniform1f(uTime, reduce ? 24.0 : 8.0 + t * 0.001);
        gl.uniform2f(uMouse, mx, my);
        gl.uniform1f(uScroll, p);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        drawn = true;
      },
    };
  };

  const initHero = (root = document) => {
    $$('[data-hero]', root).forEach((hero) => {
      if (hero._hero) return;
      hero._hero = true;
      let tx = 0;
      let ty = 0;
      let mx = 0;
      let my = 0;
      let visible = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          visible = en.isIntersecting;
        }).observe(hero);
      }
      if (!isTouch && !reduce) {
        hero.addEventListener('pointermove', (e) => {
          const r = hero.getBoundingClientRect();
          tx = (e.clientX - r.left) / r.width - 0.5;
          ty = (e.clientY - r.top) / r.height - 0.5;
        });
        hero.addEventListener('pointerleave', () => {
          tx = 0;
          ty = 0;
        });
      }
      const canvas = $('[data-hero-canvas]', hero);
      const smoke = canvas ? createSmoke(canvas) : null;
      if (canvas && !smoke) canvas.remove();

      onTick((t) => {
        if (!visible) return;
        const r = hero.getBoundingClientRect();
        const p = reduce ? 0 : clamp(-r.top / r.height, 0, 1);
        mx += (tx - mx) * 0.05;
        my += (ty - my) * 0.05;
        hero.style.setProperty('--p', p.toFixed(4));
        hero.style.setProperty('--mx', mx.toFixed(4));
        hero.style.setProperty('--my', my.toFixed(4));
        if (smoke) smoke.render(t, mx, my, p);
      }, hero);
    });
  };

  /* ---------------------------------------------------------------------
     Horizontal pinned product scroll
     --------------------------------------------------------------------- */
  const initHScroll = (root = document) => {
    $$('[data-hscroll]', root).forEach((sec) => {
      if (sec._hs) return;
      sec._hs = true;
      const track = $('[data-hscroll-track]', sec);
      const count = $('[data-hscroll-count]', sec);
      const items = $$('.hs__item', sec);
      const mq = window.matchMedia('(min-width: 990px)');
      let dist = 0;
      let active = false;
      let lastP = -1;

      const measure = () => {
        active = mq.matches && !reduce;
        sec.classList.toggle('is-pinned', active);
        if (!active) {
          sec.style.height = '';
          track.style.transform = '';
          return;
        }
        dist = Math.max(0, track.scrollWidth - html.clientWidth);
        sec.style.height = `${dist + window.innerHeight}px`;
        lastP = -1;
      };
      measure();
      window.addEventListener('resize', measure);
      window.addEventListener('load', measure);
      if (document.fonts) document.fonts.ready.then(measure);

      onTick(() => {
        let p;
        if (active) {
          const r = sec.getBoundingClientRect();
          if (r.bottom < -50 || r.top > window.innerHeight + 50) return;
          p = clamp(-r.top / (dist || 1), 0, 1);
          if (p !== lastP) track.style.transform = `translate3d(${(-p * dist).toFixed(2)}px, 0, 0)`;
        } else {
          const max = track.scrollWidth - track.clientWidth;
          p = max > 0 ? track.scrollLeft / max : 0;
        }
        if (p === lastP) return;
        lastP = p;
        sec.style.setProperty('--hs-p', p.toFixed(4));
        if (count && items.length) count.textContent = pad(Math.min(items.length, Math.floor(p * items.length) + 1));
      }, sec);
    });
  };

  /* ---------------------------------------------------------------------
     Scroll-scrubbed manifesto text
     --------------------------------------------------------------------- */
  const initScrub = (root = document) => {
    $$('[data-scrub]', root).forEach((el) => {
      if (el._scrub) return;
      el._scrub = true;
      const words = [];
      const walk = (node) => {
        Array.from(node.childNodes).forEach((n) => {
          if (n.nodeType === 3) {
            const frag = document.createDocumentFragment();
            n.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) {
                frag.appendChild(document.createTextNode(' '));
                return;
              }
              const s = document.createElement('span');
              s.className = 'sw';
              s.textContent = part;
              words.push(s);
              frag.appendChild(s);
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1) {
            walk(n);
          }
        });
      };
      walk(el);
      if (reduce) {
        words.forEach((w) => {
          w.style.opacity = 1;
        });
        return;
      }
      let visible = false;
      new IntersectionObserver(
        ([en]) => {
          visible = en.isIntersecting;
        },
        { rootMargin: '20% 0px' }
      ).observe(el);
      let lastN = -1;
      onTick(() => {
        if (!visible) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const p = clamp((vh * 0.82 - r.top) / (r.height + vh * 0.2), 0, 1);
        const n = p * words.length;
        if (Math.abs(n - lastN) < 0.02) return;
        lastN = n;
        for (let i = 0; i < words.length; i++) {
          const v = 0.14 + clamp(n - i, 0, 1) * 0.86;
          if (words[i]._o !== v) {
            words[i]._o = v;
            words[i].style.opacity = v.toFixed(3);
          }
        }
      }, el);
    });
  };

  /* ---------------------------------------------------------------------
     Parallax, eclipse, ritual
     --------------------------------------------------------------------- */
  const initParallax = (root = document) => {
    if (reduce) return;
    $$('[data-parallax]', root).forEach((el) => {
      if (el._px) return;
      el._px = true;
      const speed = parseFloat(el.dataset.parallax) || 0.12;
      const parent = el.parentElement;
      onTick(() => {
        const r = parent.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom < -100 || r.top > vh + 100) return;
        const offset = r.top + r.height / 2 - vh / 2;
        el.style.transform = `translate3d(0, ${(-offset * speed).toFixed(2)}px, 0)`;
      }, el);
    });
  };

  const initEclipse = (root = document) => {
    $$('[data-eclipse]', root).forEach((sec) => {
      if (sec._ecl) return;
      sec._ecl = true;
      if (reduce) {
        sec.style.setProperty('--p', '0.5');
        sec.style.setProperty('--pk', '1');
        return;
      }
      onTick(() => {
        const r = sec.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom < 0 || r.top > vh) return;
        const p = clamp(-r.top / (r.height - vh), 0, 1);
        const k = clamp(1 - Math.abs(p - 0.5) * 2.3, 0, 1);
        const pk = k * k * (3 - 2 * k);
        sec.style.setProperty('--p', p.toFixed(4));
        sec.style.setProperty('--pk', pk.toFixed(4));
      }, sec);
    });
  };

  const initRitual = (root = document) => {
    $$('[data-ritual]', root).forEach((sec) => {
      if (sec._rit) return;
      sec._rit = true;
      const arts = $$('[data-ritual-art]', sec);
      const insets = $$('[data-ritual-sync]', sec);
      const steps = $$('[data-ritual-step]', sec);
      const num = $('[data-ritual-num]', sec);
      const visual = $('.ritual__visual', sec);
      let current = -1;
      const set = (i) => {
        if (i === current) return;
        current = i;
        arts.forEach((a, j) => {
          a.classList.toggle('is-active', j === i);
          a.classList.toggle('is-past', j < i);
        });
        insets.forEach((el) => el.classList.toggle('is-active', parseInt(el.dataset.index, 10) === i));
        if (num) {
          num.textContent = pad(i + 1);
          if (num.animate && !reduce) {
            num.animate([{ transform: 'translateY(40%)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 800, easing: 'cubic-bezier(.16,1,.3,1)' });
          }
        }
        const tint = steps[i] && steps[i].style.getPropertyValue('--tint');
        if (tint && visual) visual.style.setProperty('--ritual-tint', tint);
      };
      if ('IntersectionObserver' in window) {
        const obs = new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => {
              if (en.isIntersecting) set(parseInt(en.target.dataset.index, 10) || 0);
            });
          },
          { rootMargin: '-45% 0px -45% 0px' }
        );
        steps.forEach((s) => obs.observe(s));
      }
      set(0);
    });
  };

  /* ---------------------------------------------------------------------
     Fit text (footer wordmark)
     --------------------------------------------------------------------- */
  const initFit = (root = document) => {
    $$('[data-fit-text]', root).forEach((el) => {
      if (el._fit) return;
      el._fit = true;
      const inner = el.firstElementChild;
      if (!inner) return;
      const fit = () => {
        el.style.fontSize = '';
        const cs = getComputedStyle(el);
        const avail = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        const w = inner.getBoundingClientRect().width;
        if (!w || !avail) return;
        const size = parseFloat(cs.fontSize) * (avail / w) * 0.995;
        el.style.fontSize = `${size}px`;
      };
      fit();
      window.addEventListener('resize', fit);
      if (document.fonts) document.fonts.ready.then(fit);
    });
  };

  /* ---------------------------------------------------------------------
     Accordions (animated <details>)
     --------------------------------------------------------------------- */
  const initAccordions = (root = document) => {
    if (reduce) return;
    $$('details.acc', root).forEach((d) => {
      if (d._acc) return;
      d._acc = true;
      const summary = $('summary', d);
      const content = $('.acc__content', d);
      if (!summary || !content) return;
      const easing = 'cubic-bezier(.16,1,.3,1)';
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (d._anim) d._anim.cancel();
        if (d.open && !d.classList.contains('is-closing')) {
          const h = content.offsetHeight;
          d.classList.add('is-closing');
          d._anim = content.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 520, easing });
          d._anim.onfinish = () => {
            d.open = false;
            d.classList.remove('is-closing');
            d._anim = null;
          };
        } else {
          d.classList.remove('is-closing');
          d.open = true;
          const h = content.offsetHeight;
          d._anim = content.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 700, easing });
          d._anim.onfinish = () => {
            d._anim = null;
          };
        }
      });
    });
  };

  /* ---------------------------------------------------------------------
     Drawers (cart, filters) + search overlay
     --------------------------------------------------------------------- */
  const Drawers = {
    current: null,
    lastFocus: null,
    open(name) {
      const d = $(`[data-drawer="${name}"]`);
      if (!d) return;
      if (this.current && this.current !== d) this.close();
      if (this.current === d) return;
      this.current = d;
      this.lastFocus = document.activeElement;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      setTimeout(() => {
        const f = $('.drawer__panel button, .drawer__panel a[href], .drawer__panel input', d);
        if (f) f.focus({ preventScroll: true });
      }, 80);
    },
    close() {
      const d = this.current;
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      this.current = null;
      lockScroll(false);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    },
  };

  const Search = {
    el: null,
    open() {
      if (!this.el || this.el.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      setTimeout(() => {
        const input = $('[data-search-input]', this.el);
        if (input) input.focus({ preventScroll: true });
      }, 120);
    },
    close() {
      if (!this.el || !this.el.classList.contains('is-open')) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    },
  };

  const initSearch = () => {
    const ov = $('[data-search]');
    if (!ov) return;
    Search.el = ov;
    const input = $('[data-search-input]', ov);
    const results = $('[data-search-results]', ov);
    let timer;
    let ctrl;

    const imgUrl = (url, width) => (url ? url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width : '');

    const render = (items, q) => {
      if (!items.length) {
        results.innerHTML = `<p class="search-ov__empty">${esc(S.noResults || 'No matches')} — “${esc(q)}”</p>`;
        return;
      }
      const list = items
        .map((p, i) => {
          const src = (p.featured_image && p.featured_image.url) || p.image || '';
          const price = p.price ? money(Math.round(parseFloat(p.price) * 100)) : '';
          return `<li><a class="sres" href="${esc(p.url)}" style="--i:${i}">
              <span class="sres__img">${src ? `<img src="${esc(imgUrl(src, 240))}" alt="" loading="lazy">` : ''}</span>
              <span class="sres__text"><span class="sres__title">${esc(p.title)}</span><span class="price">${esc(price)}</span></span>
              <span class="sres__go" aria-hidden="true">→</span>
            </a></li>`;
        })
        .join('');
      results.innerHTML = `<p class="label search-ov__heading">${esc(S.searchResults || 'Products')}</p>
        <ul class="search-ov__list">${list}</ul>
        <a class="btn search-ov__all" href="${esc(R.search)}?q=${encodeURIComponent(q)}&type=product"><span class="btn__text" data-text="${esc(S.searchAll || 'View all results')}">${esc(S.searchAll || 'View all results')}</span></a>`;
    };

    const query = async (q) => {
      if (ctrl) ctrl.abort();
      ctrl = 'AbortController' in window ? new AbortController() : null;
      try {
        const url = `${R.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, ctrl ? { signal: ctrl.signal } : {});
        const data = await res.json();
        const items = (data.resources && data.resources.results && data.resources.results.products) || [];
        render(items, q);
      } catch (e) {
        /* aborted or offline */
      }
    };

    const update = () => {
      clearTimeout(timer);
      const q = input.value.trim();
      ov.classList.toggle('has-query', !!q);
      if (!q) {
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(() => query(q), 220);
    };

    input.addEventListener('input', update);
  };

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    },
    async add(form, btn) {
      const fd = new FormData(form);
      fd.append('sections', this.sectionIds().join(','));
      fd.append('sections_url', window.location.pathname);
      if (btn) btn.classList.add('is-loading');
      try {
        const res = await fetch(`${R.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || S.error);
        if (T.cartType === 'page') {
          window.location.href = R.cart;
          return;
        }
        this.render(data.sections);
        Drawers.open('cart');
      } catch (err) {
        toast(err.message || S.error);
      } finally {
        if (btn) btn.classList.remove('is-loading');
      }
    },
    async change(line, quantity) {
      const res = await fetch(`${R.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.error);
      this.render(data.sections);
    },
    render(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const markup = sections[id];
        if (!markup) return;
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        if (id === 'cart-drawer') {
          const fresh = $('[data-cart-inner]', doc);
          const cur = $('[data-cart-inner]');
          if (fresh && cur) {
            cur.replaceWith(fresh);
            this.updateCount(fresh.dataset.count);
            initMagnetic(fresh);
          }
        } else {
          const fresh = $('[data-cart-page]', doc);
          const cur = $('[data-cart-page]');
          if (fresh && cur) {
            cur.replaceWith(fresh);
            revealNow(fresh);
            initMagnetic(fresh);
          }
        }
      });
    },
    updateCount(n) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.classList.toggle('is-empty', String(n) === '0');
        el.classList.add('is-bump');
        setTimeout(() => el.classList.remove('is-bump'), 600);
      });
    },
  };

  /* ---------------------------------------------------------------------
     Wishlist: product handles saved on this device
     --------------------------------------------------------------------- */
  const Wishlist = {
    key: 'fitoor:wishlist',
    read() {
      try {
        const list = JSON.parse(localStorage.getItem(this.key) || '[]');
        return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(this.key, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable */
      }
      this.sync();
      document.dispatchEvent(new CustomEvent('wishlist:change'));
    },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.unshift(handle);
      this.write(list);
      return i === -1;
    },
    sync(root = document) {
      const list = this.read();
      $$('[data-wish]', root).forEach((b) => {
        const on = list.includes(b.dataset.wish);
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', on ? S.wishRemove : S.wishAdd);
      });
      $$('[data-wish-count]').forEach((el) => {
        el.textContent = list.length;
        el.classList.toggle('is-empty', !list.length);
      });
    },
  };

  /* Wishlist page: renders each saved handle through the wishlist-item section */
  const initWishlistPage = (root = document) => {
    $$('[data-wishlist]', root).forEach((el) => {
      if (el._wl) return;
      el._wl = true;
      const grid = $('[data-wishlist-grid]', el);
      const count = $('[data-wishlist-count]', el);
      const base = (R.root || '/').replace(/\/$/, '');

      const update = () => {
        const n = $$('.ptile', grid).length;
        el.classList.toggle('is-empty', n === 0);
        if (count) count.textContent = (n === 1 ? count.dataset.one : count.dataset.other).replace('#', n);
      };

      const render = async () => {
        const list = Wishlist.read();
        el.classList.add('is-loading');
        const tiles = await Promise.all(
          list.map(async (handle) => {
            try {
              const res = await fetch(`${base}/products/${encodeURIComponent(handle)}?section_id=wishlist-item`);
              if (res.status === 404) return { handle, gone: true };
              if (!res.ok) return null;
              const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
              return { handle, tile: $('.ptile', doc) };
            } catch (e) {
              return null;
            }
          })
        );
        const gone = tiles.filter((t) => t && t.gone).map((t) => t.handle);
        if (gone.length) Wishlist.write(Wishlist.read().filter((h) => !gone.includes(h)));
        grid.replaceChildren(...tiles.filter((t) => t && t.tile).map((t) => t.tile));
        Wishlist.sync(grid);
        el.classList.remove('is-loading');
        update();
      };

      document.addEventListener('wishlist:change', () => {
        const list = Wishlist.read();
        $$('[data-wish]', grid).forEach((b) => {
          if (!list.includes(b.dataset.wish)) {
            const tile = b.closest('.ptile');
            if (tile) tile.remove();
          }
        });
        update();
      });

      render();
    });
  };

  /* ---------------------------------------------------------------------
     Tabs (collection tabs section)
     --------------------------------------------------------------------- */
  const initTabs = (root = document) => {
    $$('[data-tabs]', root).forEach((el) => {
      if (el._tabs) return;
      el._tabs = true;
      const tabs = $$('[data-tab]', el);
      const panels = $$('[data-panel]', el);
      if (tabs.length < 2) return;

      const select = (i, focus) => {
        tabs.forEach((t, n) => {
          const on = n === i;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const bar = t.parentElement;
          if (on && bar.scrollWidth > bar.clientWidth) {
            bar.scrollTo({ left: t.offsetLeft - bar.clientWidth / 2 + t.offsetWidth / 2, behavior: reduce ? 'auto' : 'smooth' });
          }
        });
        panels.forEach((p, n) => (p.hidden = n !== i));
        if (focus) tabs[i].focus();
        // rails inside a panel that was hidden have to measure themselves again
        window.dispatchEvent(new Event('resize'));
      };

      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(i));
        t.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          select((i + dir + tabs.length) % tabs.length, true);
        });
      });
    });
  };

  /* ---------------------------------------------------------------------
     Product
     --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     Product gallery: swipeable track with thumbnails, arrows and dots
     --------------------------------------------------------------------- */
  const initGallery = (sec) => {
    const root = $('[data-gallery]', sec);
    if (!root) return null;
    const track = $('[data-gallery-track]', root);
    const slides = $$('[data-slide]', root);
    const thumbs = $$('[data-gallery-go]', root);
    const dots = $$('.pgx__dot', root);
    const idx = $('[data-gallery-index]', root);
    if (!track || slides.length < 2) return { showMedia() {} };
    let cur = 0;
    let settle = null;

    const mark = (i) => {
      cur = i;
      slides.forEach((el, n) => el.classList.toggle('is-active', n === i));
      thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      if (idx) idx.textContent = pad(i + 1);
      const th = thumbs[i];
      if (th && th.parentElement) {
        const box = th.parentElement;
        const vertical = box.scrollHeight > box.clientHeight + 2;
        if (vertical) box.scrollTo({ top: th.offsetTop - box.clientHeight / 2 + th.clientHeight / 2, behavior: 'smooth' });
        else box.scrollTo({ left: th.offsetLeft - box.clientWidth / 2 + th.clientWidth / 2, behavior: 'smooth' });
      }
      slides.forEach((el, n) => n !== i && $$('video', el).forEach((v) => v.pause && v.pause()));
    };

    const go = (i, smooth = true) => {
      const n = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[n].offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      mark(n);
    };

    track.addEventListener(
      'scroll',
      () => {
        clearTimeout(settle);
        settle = setTimeout(() => {
          const n = clamp(Math.round(track.scrollLeft / track.clientWidth), 0, slides.length - 1);
          if (n !== cur) mark(n);
        }, 80);
      },
      { passive: true }
    );
    root.addEventListener('click', (e) => {
      const t = e.target.closest('[data-gallery-go]');
      if (t) go(parseInt(t.dataset.galleryGo, 10));
      if (e.target.closest('[data-gallery-next]')) go(cur + 1);
      if (e.target.closest('[data-gallery-prev]')) go(cur - 1);
    });
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(cur + 1);
      if (e.key === 'ArrowLeft') go(cur - 1);
    });
    window.addEventListener('resize', () => track.scrollTo({ left: slides[cur].offsetLeft, behavior: 'auto' }));

    return {
      showMedia(id) {
        const i = slides.findIndex((el) => el.dataset.mediaId === String(id));
        if (i > -1) go(i);
      },
    };
  };

  /* Sticky add-to-bag bar: shows once the main buy buttons scroll out of view */
  const initStickyBuy = (sec) => {
    const bar = $('[data-sticky-buy]', sec);
    const buy = $('[data-buy]', sec);
    if (!bar || !buy || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([en]) => {
      const show = !en.isIntersecting && en.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      const btn = $('[data-sticky-btn]', bar);
      if (btn) btn.tabIndex = show ? 0 : -1;
    }).observe(buy);
  };

  const initProduct = (root = document) => {
    $$('[data-product]', root).forEach((sec) => {
      if (sec._prod) return;
      sec._prod = true;
      const gallery = initGallery(sec);
      initStickyBuy(sec);

      const picker = $('[data-variant-picker]', sec);
      const json = $('[data-variants-json]', sec);
      if (!picker || !json) return;
      let variants = [];
      try {
        variants = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      const idInput = $('[data-variant-id]', sec);
      const btn = $('[data-add-btn]', sec);
      const btnText = $('[data-add-text]', sec);
      const btnPrice = $('[data-add-price]', sec);
      const priceWrap = $('[data-product-price]', sec);
      const stickyPrice = $('[data-sticky-price]', sec);
      const stickyBtn = $('[data-sticky-btn]', sec);

      const setBtn = (label, disabled) => {
        if (stickyBtn) {
          stickyBtn.disabled = disabled;
          stickyBtn.textContent = label;
        }
        if (!btn) return;
        btn.disabled = disabled;
        if (btnText) {
          btnText.textContent = label;
          btnText.setAttribute('data-text', label);
        }
      };

      const update = (v) => {
        if (!v) {
          setBtn(S.unavailable, true);
          return;
        }
        if (idInput) idInput.value = v.id;
        setBtn(v.available ? S.addToCart : S.soldOut, !v.available);
        if (btnPrice) btnPrice.textContent = money(v.price);
        if (stickyPrice) stickyPrice.textContent = money(v.price);
        if (priceWrap) {
          const sale = v.compare_at_price && v.compare_at_price > v.price;
          priceWrap.innerHTML = `<span class="price${sale ? ' price--sale' : ''}"><span class="price__now">${esc(money(v.price))}</span>${
            sale ? `<s class="price__was">${esc(money(v.compare_at_price))}</s>` : ''
          }</span>`;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
        if (v.featured_media && gallery) gallery.showMedia(v.featured_media.id);
      };

      picker.addEventListener('change', () => {
        const groups = $$('fieldset', picker);
        const selected = groups.map((fs) => {
          const c = $('input:checked', fs);
          return c ? c.value : null;
        });
        $$('[data-option-value]', picker).forEach((el) => {
          el.textContent = selected[parseInt(el.dataset.optionValue, 10)] || '';
        });
        update(variants.find((v) => v.options.every((o, i) => o === selected[i])));
      });
    });
  };

  const initRecs = (root = document) => {
    $$('[data-recs]', root).forEach(async (el) => {
      if (el._recs || !el.dataset.url || el.children.length) return;
      el._recs = true;
      try {
        const res = await fetch(el.dataset.url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = $('[data-recs]', doc);
        if (fresh && fresh.children.length) {
          el.innerHTML = fresh.innerHTML;
          initAll(el);
        }
      } catch (e) {
        /* ignore */
      }
    });
  };

  /* ---------------------------------------------------------------------
     Slideshow: native swipe (scroll-snap) + arrows, dots, autoplay
     --------------------------------------------------------------------- */
  const initSlides = (root = document) => {
    $$('[data-slides]', root).forEach((el) => {
      if (el._slides) return;
      el._slides = true;
      const track = $('[data-slides-track]', el);
      const slides = $$('[data-slide]', el);
      const dots = $$('[data-slides-dot]', el);
      const counter = $('[data-slides-count]', el);
      if (!track || !slides.length) return;

      const interval = Math.max(3, parseFloat(el.dataset.interval) || 6) * 1000;
      let autoplay = el.dataset.autoplay === 'true' && !reduce && slides.length > 1;
      let index = -1;
      let elapsed = 0;
      let last = 0;
      let hover = false;
      let holdUntil = 0;
      let visible = true;
      let settle = null;

      const setActive = (i) => {
        if (i === index) return;
        index = i;
        elapsed = 0;
        slides.forEach((s, n) => {
          s.classList.toggle('is-active', n === i);
          s.inert = n !== i;
        });
        dots.forEach((d, n) => {
          d.classList.toggle('is-active', n === i);
          d.setAttribute('aria-current', n === i ? 'true' : 'false');
          d.style.setProperty('--prog', n < i ? 1 : 0);
        });
        if (counter) counter.textContent = pad(i + 1);
      };

      const goTo = (i, smooth = true) => {
        const n = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[n].offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
        setActive(n);
      };

      const pause = (ms = interval) => {
        holdUntil = performance.now() + ms;
      };

      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(settle);
          settle = setTimeout(() => setActive(clamp(Math.round(track.scrollLeft / track.clientWidth), 0, slides.length - 1)), 90);
        },
        { passive: true }
      );
      track.addEventListener('pointerdown', () => pause(), { passive: true });
      track.addEventListener('touchstart', () => pause(), { passive: true });
      if (!isTouch) {
        el.addEventListener('mouseenter', () => (hover = true));
        el.addEventListener('mouseleave', () => (hover = false));
      }
      el.addEventListener('focusin', () => (hover = true));
      el.addEventListener('focusout', () => (hover = false));

      const prev = $('[data-slides-prev]', el);
      const next = $('[data-slides-next]', el);
      if (prev) prev.addEventListener('click', () => { pause(); goTo(index - 1); });
      if (next) next.addEventListener('click', () => { pause(); goTo(index + 1); });
      dots.forEach((d, n) => d.addEventListener('click', () => { pause(); goTo(n); }));

      el.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') goTo(index - 1);
        if (e.key === 'ArrowRight') goTo(index + 1);
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          visible = en.isIntersecting;
        }).observe(el);
      }

      window.addEventListener('resize', () => {
        track.scrollTo({ left: slides[index].offsetLeft, behavior: 'auto' });
      });

      if (designMode) {
        el.addEventListener('shopify:block:select', (e) => {
          const n = slides.indexOf(e.target.closest('[data-slide]'));
          if (n < 0) return;
          autoplay = false;
          goTo(n, false);
        });
      }

      onTick((t) => {
        const dt = last ? Math.min(t - last, 100) : 0;
        last = t;
        if (!autoplay || hover || !visible || document.hidden || t < holdUntil) return;
        elapsed += dt;
        const dot = dots[index];
        if (dot) dot.style.setProperty('--prog', Math.min(1, elapsed / interval).toFixed(3));
        if (elapsed >= interval) goTo(index + 1);
      }, el);

      setActive(0);
    });
  };

  /* ---------------------------------------------------------------------
     Horizontal rails: arrows + progress bar
     --------------------------------------------------------------------- */
  const initRails = (root = document) => {
    $$('[data-rail-wrap]', root).forEach((wrap) => {
      if (wrap._rail) return;
      wrap._rail = true;
      const rail = $('[data-rail]', wrap);
      if (!rail) return;
      const prev = $('[data-rail-prev]', wrap);
      const next = $('[data-rail-next]', wrap);
      const bar = $('[data-rail-bar]', wrap);

      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        wrap.classList.toggle('is-static', max <= 2);
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max - 2;
        if (bar) {
          bar.style.setProperty('--w', Math.min(1, rail.clientWidth / Math.max(1, rail.scrollWidth)).toFixed(4));
          bar.style.setProperty('--x', (max > 0 ? rail.scrollLeft / max : 0).toFixed(4));
        }
      };

      const step = (dir) => {
        const item = rail.firstElementChild;
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        const w = item ? item.getBoundingClientRect().width + gap : rail.clientWidth;
        const n = Math.max(1, Math.floor((rail.clientWidth + gap) / w));
        rail.scrollBy({ left: dir * n * w, behavior: reduce ? 'auto' : 'smooth' });
      };

      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  /* ---------------------------------------------------------------------
     Quick-buy sheets on product tiles
     --------------------------------------------------------------------- */
  const initQuickBuy = () => {
    const closeAll = (except) => $$('details[data-quick][open]').forEach((d) => d !== except && (d.open = false));

    document.addEventListener(
      'toggle',
      (e) => {
        const d = e.target;
        if (d instanceof HTMLDetailsElement && d.matches('[data-quick]') && d.open) closeAll(d);
      },
      true
    );

    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const close = t.closest('[data-quick-close]');
      if (close) {
        const d = close.closest('details[data-quick]');
        if (d) d.open = false;
        return;
      }
      if (!t.closest('details[data-quick]')) closeAll();
    });

    document.addEventListener('submit', (e) => {
      const d = e.target.closest && e.target.closest('details[data-quick]');
      if (d) setTimeout(() => (d.open = false), 600);
    });
  };

  /* ---------------------------------------------------------------------
     Collection toolbar: dropdown filters, auto-apply, phone layout toggle
     --------------------------------------------------------------------- */
  const initPlp = (root = document) => {
    $$('[data-plp]', root).forEach((el) => {
      if (el._plp) return;
      el._plp = true;
      const form = $('[data-plp-form]', el);
      const submit = (f) => {
        if (!f) return;
        // keep the URL tidy: drop empty price fields
        $$('input', f).forEach((i) => {
          if (i.type !== 'checkbox' && i.value === '') i.disabled = true;
        });
        f.submit();
      };
      if (form) {
        form.addEventListener('change', (e) => {
          if (e.target.matches('[data-plp-auto]')) submit(form);
        });
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          submit(form);
        });
      }
      const sort = $('[data-plp-sort]', el);
      if (sort) {
        sort.addEventListener('change', () => {
          const f = sort.form;
          if (!f) return;
          const hidden = $('input[name="sort_by"]', f) || f.appendChild(Object.assign(document.createElement('input'), { type: 'hidden', name: 'sort_by' }));
          hidden.value = sort.value;
          f.submit();
        });
      }

      const grid = $('[data-plp-grid]', el);
      const btns = $$('[data-plp-cols]', el);
      const setCols = (n) => {
        if (grid) grid.dataset.cols = n;
        btns.forEach((b) => {
          const on = b.dataset.plpCols === String(n);
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', String(on));
        });
        try {
          localStorage.setItem('fitoor:cols', n);
        } catch (e) {
          /* storage unavailable */
        }
      };
      btns.forEach((b) => b.addEventListener('click', () => setCols(b.dataset.plpCols)));
      try {
        const saved = localStorage.getItem('fitoor:cols');
        if (saved) setCols(saved);
      } catch (e) {
        /* storage unavailable */
      }
    });
  };

  /* Close other dropdown filters when one opens, and on outside click */
  const initDropdowns = () => {
    document.addEventListener(
      'toggle',
      (e) => {
        const d = e.target;
        if (d instanceof HTMLDetailsElement && d.matches('[data-dd]') && d.open) {
          $$('details[data-dd][open]').forEach((o) => o !== d && (o.open = false));
        }
      },
      true
    );
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest('details[data-dd]')) $$('details[data-dd][open]').forEach((o) => (o.open = false));
      const opener = t.closest('[data-quick-open]');
      if (opener) {
        const d = $('details[data-quick]', opener.closest('.ptile'));
        if (d) {
          e.stopPropagation();
          d.open = true;
        }
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') $$('details[data-dd][open], details[data-quick][open]').forEach((o) => (o.open = false));
    });
  };

  /* ---------------------------------------------------------------------
     Read-more text
     --------------------------------------------------------------------- */
  const initReadMore = (root = document) => {
    $$('[data-readmore]', root).forEach((el) => {
      if (el._rm) return;
      el._rm = true;
      const body = $('[data-readmore-body]', el);
      const btn = $('[data-readmore-toggle]', el);
      if (!body || !btn) return;
      if (body.scrollHeight <= body.clientHeight + 8) {
        el.classList.add('is-full');
        return;
      }
      const label = $('[data-readmore-label]', btn);
      btn.addEventListener('click', () => {
        const open = !el.classList.contains('is-open');
        el.classList.toggle('is-open', open);
        body.style.maxHeight = open ? `${body.scrollHeight}px` : '';
        btn.setAttribute('aria-expanded', String(open));
        if (label) label.textContent = open ? btn.dataset.less : btn.dataset.more;
      });
    });
  };

  /* ---------------------------------------------------------------------
     Delegated events
     --------------------------------------------------------------------- */
  const focusables = (scope) =>
    $$('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', scope).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  const initDelegates = () => {
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;

      const wish = t.closest('[data-wish]');
      if (wish) {
        e.preventDefault();
        const on = Wishlist.toggle(wish.dataset.wish);
        toast(on ? S.wishAdded : S.wishRemoved);
        if (on) {
          wish.classList.remove('is-pop');
          void wish.offsetWidth;
          wish.classList.add('is-pop');
        }
        return;
      }

      const menuBtn = t.closest('[data-menu-toggle]');
      if (menuBtn) {
        setMenu(!html.classList.contains('menu-open'));
        return;
      }
      if (html.classList.contains('menu-open') && t.closest('[data-menu] a')) setMenu(false);

      if (t.closest('[data-search-open]')) {
        e.preventDefault();
        setMenu(false);
        Search.open();
        return;
      }
      if (t.closest('[data-search-close]')) {
        e.preventDefault();
        Search.close();
        return;
      }

      const opener = t.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        Drawers.open(opener.getAttribute('data-drawer-open'));
        return;
      }
      if (t.closest('[data-drawer-close]')) {
        e.preventDefault();
        Drawers.close();
        return;
      }

      const cartLink = t.closest('[data-cart-open]');
      if (cartLink && T.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
        e.preventDefault();
        setMenu(false);
        Drawers.open('cart');
        return;
      }

      const qtyStep = t.closest('[data-qty-step]');
      if (qtyStep) {
        const input = $('input', qtyStep.closest('[data-qty]'));
        if (input) input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(qtyStep.dataset.qtyStep, 10));
        return;
      }

      const lineStep = t.closest('[data-line-step]');
      const lineRemove = t.closest('[data-line-remove]');
      if (lineStep || lineRemove) {
        const line = t.closest('[data-line]');
        if (!line) return;
        e.preventDefault();
        const input = $('[data-line-input]', line);
        const current = parseInt(input && input.value, 10) || 0;
        const qty = lineRemove ? 0 : Math.max(0, current + parseInt(lineStep.dataset.lineStep, 10));
        line.classList.add('is-busy');
        Cart.change(parseInt(line.dataset.line, 10), qty).catch((err) => {
          toast(err.message || S.error);
          line.classList.remove('is-busy');
        });
        return;
      }

      if (t.closest('[data-demo-add]')) {
        e.preventDefault();
        toast(S.demo);
        return;
      }

      if (t.closest('[data-scroll-top]')) {
        e.preventDefault();
        scrollToTarget(0);
        return;
      }

      const anchor = t.closest('a[href^="#"]');
      if (anchor && lenis && !anchor.closest('.acct')) {
        const id = anchor.getAttribute('href').slice(1);
        const target = id && document.getElementById(id);
        if (target) {
          e.preventDefault();
          scrollToTarget(target, -80);
          if (target.matches('main, [tabindex]')) target.focus({ preventScroll: true });
        }
      }
    });

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.matches('[data-line-input]')) {
        const line = t.closest('[data-line]');
        if (!line) return;
        line.classList.add('is-busy');
        Cart.change(parseInt(line.dataset.line, 10), Math.max(0, parseInt(t.value, 10) || 0)).catch((err) => {
          toast(err.message || S.error);
          line.classList.remove('is-busy');
        });
      }
      if (t.matches('select[data-auto-submit]') && t.form) t.form.submit();
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest && e.target.closest('form[data-ajax-cart]');
      if (!form) return;
      e.preventDefault();
      Cart.add(form, $('[type="submit"]', form));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        Search.close();
        Drawers.close();
        setMenu(false);
        return;
      }
      if (e.key !== 'Tab') return;
      let scope = null;
      if (Drawers.current) scope = $('.drawer__panel', Drawers.current);
      else if (Search.el && Search.el.classList.contains('is-open')) scope = Search.el;
      if (!scope) return;
      const f = focusables(scope);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  };

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function initAll(root = document) {
    initReveals(root);
    initMagnetic(root);
    initSpotlight(root);
    initMarquee(root);
    initHero(root);
    initSlides(root);
    initRails(root);
    initReadMore(root);
    initPlp(root);
    initHScroll(root);
    initScrub(root);
    initParallax(root);
    initRitual(root);
    initEclipse(root);
    initFit(root);
    initAccordions(root);
    initProduct(root);
    initRecs(root);
    initTabs(root);
    initWishlistPage(root);
    Wishlist.sync(root);
  }

  const boot = () => {
    initLenis();
    requestAnimationFrame(frame);
    initDelegates();
    initQuickBuy();
    initDropdowns();
    initTransitions();
    initHeader();
    initCursor();
    initSearch();
    initAll(document);
    initPreloader();

    document.addEventListener('shopify:section:load', (e) => {
      initHeader(e.target);
      initAll(e.target);
      revealNow(e.target);
    });

    // Theme editor: show the tab whose block is selected
    document.addEventListener('shopify:block:select', (e) => {
      const tab = e.target.closest && e.target.closest('[data-tab]');
      if (tab) tab.click();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
